import React, { useContext, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import GlobalPopUp from "../../../components/utility/globalPopUp";
import SubChatbotExecutionLogData from "../threadView/components/subChatbotExecutionLogData";
import ThreadExecutionTimeLog from "../threadView/components/threadExecutionTimeLog";
import OpenAiStepsData from "../threadView/components/openAiStepsData";
import CreateTicketPopup from "../threadInfo/components/threadTicket/createTicketPopup";
import PreviewAndSendPopup from "../threadView/components/chatInput/cannedResponse/components/previewAndSendPopup";
import type {
    currentActiveThreadChatbotThreadData,
    executionTimeLog,
    openAIStepsDataInterface,
    sendCannedResponsePayload,
    sendTemplatePayload,
    subChatbotDataInterface,
    uploadedMedia,
} from "../../../models/axiosModel";
import agent from "../../../services/api";
import { UserContext, UserContextPayload } from "../../../context/userContext";
import { useRuleBasedInboxStore } from "../../../store/ruleBasedInboxStore";
import { useTicketStore } from "../../../store/ticketStore";
import useGenericHook from "../../../hooks/useGenericHook";

type HandleSendMessageManually = (
    inputValue: string,
    flagMediaMessage: boolean,
    flagCannedResponseTemplate: boolean,
    flagTemplateMessage: boolean,
    selectedMediaSet: uploadedMedia[],
    templatePayload: sendTemplatePayload | null,
    cannedResponsePayload?: sendCannedResponsePayload
) => Promise<void>;

interface ThreadLogViewModalsProps {
    handleSendMessageManually: HandleSendMessageManually;
    selectedTab: string | null;
    setSelectedTab: React.Dispatch<React.SetStateAction<string | null>>;
    openAIStepsData: openAIStepsDataInterface;
    setOpenAIStepsData: React.Dispatch<React.SetStateAction<openAIStepsDataInterface>>;
    dataToShow: any[] | null;
    setDataToShow: React.Dispatch<React.SetStateAction<any[] | null>>;
    sortOpenAIStepsData: (data: any[]) => any[];
    subChatbotExceutionLogData: subChatbotDataInterface;
    setSubChatbotExceutionLogData: React.Dispatch<React.SetStateAction<subChatbotDataInterface>>;
    threadExecutionTimeLog: executionTimeLog;
    setThreadExecutionTimeLog: React.Dispatch<React.SetStateAction<executionTimeLog>>;
}

/**
 * Owns thread-log-view popup state subscriptions (create ticket / canned preview)
 * and local OpenAI / sub-chatbot / execution-time modal UI.
 */
export const useThreadLogViewModals = () => {
    const { setFlagLoading, handleError } = useContext(UserContext) as UserContextPayload;
    const { currentActiveChatbotThread } = useRuleBasedInboxStore(
        useShallow((state) => ({
            currentActiveChatbotThread: state.currentActiveChatbotThread,
        }))
    );

    const [selectedTab, setSelectedTab] = useState<string | null>(null);
    const [openAIStepsData, setOpenAIStepsData] = useState<openAIStepsDataInterface>({
        flagpopup: false,
        data: [],
    });
    const [dataToShow, setDataToShow] = useState<any[] | null>(null);
    const [subChatbotExceutionLogData, setSubChatbotExceutionLogData] = useState<subChatbotDataInterface>({
        flagpopup: false,
        data: [],
    });
    const [threadExecutionTimeLog, setThreadExecutionTimeLog] = useState<executionTimeLog>({
        flag_popup: false,
        data: null,
    });

    const sortOpenAIStepsData = (data: any[]) => {
        return data?.sort((a: any, b: any) => (a.completed_at || a.created_at) - (b.completed_at || b.created_at));
    };

    async function getSteps(ai_execution_id: string) {
        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: true }));
        if (currentActiveChatbotThread) {
            const response = await agent.getMessageAISteps.get(currentActiveChatbotThread.chatbot_user_id, ai_execution_id);
            if (response.status) {
                setOpenAIStepsData({
                    flagpopup: true,
                    data: response.data,
                });
                if (response.data?.length > 0) {
                    const data = response.data[0];
                    if (data?.current_run_steps?.length > 0) {
                        setSelectedTab("step");
                        setDataToShow(sortOpenAIStepsData(data.current_run_steps));
                    } else if (data?.current_run?.length > 0) {
                        setSelectedTab("run");
                        setDataToShow(sortOpenAIStepsData(data.current_run));
                    } else if (data?.run_list?.length > 0) {
                        setSelectedTab("runlist");
                        setDataToShow(sortOpenAIStepsData(data.run_list));
                    } else {
                        setSelectedTab(null);
                        setDataToShow(null);
                    }
                }
            } else {
                handleError(response);
            }
        }
        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: false }));
    }

    async function getSubChatbotExceutionLog(ai_execution_id: string) {
        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: true }));
        if (currentActiveChatbotThread) {
            const response = await agent.getSubChatbotExecutionLog.get(ai_execution_id);
            if (response.status) {
                setSubChatbotExceutionLogData({
                    flagpopup: true,
                    data: response.data,
                });
            } else {
                handleError(response);
            }
        }
        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: false }));
    }

    const handleExecutionTimeLog = (thread: currentActiveThreadChatbotThreadData) => {
        setThreadExecutionTimeLog({
            flag_popup: true,
            data: thread?.chatbot_action_execution_data?.thread_exec_time_log,
        });
    };

    return {
        getSteps,
        getSubChatbotExceutionLog,
        handleExecutionTimeLog,
        selectedTab,
        setSelectedTab,
        openAIStepsData,
        setOpenAIStepsData,
        dataToShow,
        setDataToShow,
        sortOpenAIStepsData,
        subChatbotExceutionLogData,
        setSubChatbotExceutionLogData,
        threadExecutionTimeLog,
        setThreadExecutionTimeLog,
    };
};

const ThreadLogViewModals: React.FC<ThreadLogViewModalsProps> = ({
    handleSendMessageManually,
    selectedTab,
    setSelectedTab,
    openAIStepsData,
    setOpenAIStepsData,
    dataToShow,
    setDataToShow,
    sortOpenAIStepsData,
    subChatbotExceutionLogData,
    setSubChatbotExceutionLogData,
    threadExecutionTimeLog,
    setThreadExecutionTimeLog,
}) => {
    const { isScreenBelow1000, isScreenBelow600 } = useGenericHook();

    const { flagCreateTicketPopup, setFlagCreateTicketPopup } = useTicketStore(
        useShallow((state) => ({
            flagCreateTicketPopup: state.flagCreateTicketPopup,
            setFlagCreateTicketPopup: state.setFlagCreateTicketPopup,
        }))
    );

    const { flagPreviewAndSendCannedResPopup, setFlagPreviewAndSendCannedResPopup } = useRuleBasedInboxStore(
        useShallow((state) => ({
            flagPreviewAndSendCannedResPopup: state.flagPreviewAndSendCannedResPopup,
            setFlagPreviewAndSendCannedResPopup: state.setFlagPreviewAndSendCannedResPopup,
        }))
    );

    return (
        <>
            <GlobalPopUp
                open={subChatbotExceutionLogData.flagpopup}
                handleClose={() => setSubChatbotExceutionLogData((prevState) => ({ ...prevState, flagpopup: false, data: [] }))}
                paperMinWidth={isScreenBelow1000 ? "310px" : "700px"}
                paperMaxwidth={isScreenBelow1000 ? "610px" : "750px"}
                height="100%"
                flagShowCloseIcon={true}
                titlePadding="0"
            >
                <SubChatbotExecutionLogData subChatbotExceutionLogData={subChatbotExceutionLogData} />
            </GlobalPopUp>

            {threadExecutionTimeLog.flag_popup && (
                <GlobalPopUp
                    open={threadExecutionTimeLog.flag_popup}
                    handleClose={() => setThreadExecutionTimeLog({ flag_popup: false, data: null })}
                    onClose={() => setThreadExecutionTimeLog({ flag_popup: false, data: null })}
                    flagShowCloseIcon={true}
                    titlePadding="0px"
                    paperMinWidth={isScreenBelow1000 ? "310px" : "900px"}
                    paperMaxwidth={isScreenBelow1000 ? "610px" : "950px"}
                >
                    <ThreadExecutionTimeLog threadExecutionTimeLog={threadExecutionTimeLog?.data} />
                </GlobalPopUp>
            )}

            <GlobalPopUp
                open={openAIStepsData.flagpopup}
                handleClose={() => setOpenAIStepsData((prevState) => ({ ...prevState, flagpopup: false, data: [] }))}
                paperMinWidth={isScreenBelow1000 ? "310px" : "700px"}
                paperMaxwidth={isScreenBelow1000 ? "610px" : "750px"}
                height="100%"
                flagShowCloseIcon={true}
                titlePadding="0"
            >
                <OpenAiStepsData
                    selectedTab={selectedTab}
                    setSelectedTab={setSelectedTab}
                    dataToShow={dataToShow}
                    setDataToShow={setDataToShow}
                    openAIStepsData={openAIStepsData}
                    sortOpenAIStepsData={sortOpenAIStepsData}
                />
            </GlobalPopUp>

            {flagCreateTicketPopup && (
                <GlobalPopUp
                    open={flagCreateTicketPopup}
                    handleClose={() => setFlagCreateTicketPopup(false)}
                    onClose={() => setFlagCreateTicketPopup(false)}
                    paperMinWidth={isScreenBelow1000 ? "310px" : "700px"}
                    paperMaxwidth={isScreenBelow1000 ? "610px" : "750px"}
                    flagShowCloseIcon={true}
                    titlePadding="0"
                >
                    <CreateTicketPopup />
                </GlobalPopUp>
            )}

            {flagPreviewAndSendCannedResPopup && (
                <GlobalPopUp
                    open={flagPreviewAndSendCannedResPopup}
                    onClose={() => setFlagPreviewAndSendCannedResPopup(false)}
                    handleClose={() => setFlagPreviewAndSendCannedResPopup(false)}
                    flagShowCloseIcon={true}
                    paperMinWidth={isScreenBelow600 ? "310px" : "650px"}
                    paperMaxwidth={isScreenBelow600 ? "610px" : "650px"}
                    borderRadius="10px"
                    titlePadding="0px"
                    overflow="hidden"
                >
                    <PreviewAndSendPopup handleSendMessageManually={handleSendMessageManually} />
                </GlobalPopUp>
            )}
        </>
    );
};

export default ThreadLogViewModals;
